import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from 'react-query';
import { searchByPostOffice, searchByPincode } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { extractSearchQuery, generateSeoTitle, generateSeoDescription } from '../utils/searchUtils';

const AreaPincode: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const extractedQuery = extractSearchQuery(slug.replace(/-/g, ' '));

  const { data, isLoading, error } = useQuery(
    ['area-details', extractedQuery],
    async () => {
      if (extractedQuery.type === 'pincode') {
        return searchByPincode(extractedQuery.value);
      }
      return searchByPostOffice(extractedQuery.value);
    }
  );

  const postOffices = data?.[0]?.PostOffice || [];
  const firstPostOffice = postOffices[0];
  
  // Generate rich content for SEO
  const generateAreaContent = () => {
    if (!firstPostOffice) return '';

    const {
      Name,
      State,
      District,
      Division,
      Region,
      Block,
      Circle,
      Pincode
    } = firstPostOffice;

    return `
      <div class="mb-8">
        <h2 class="text-2xl font-semibold mb-4">About ${Name}</h2>
        <p class="mb-4">
          ${Name} is located in ${District}, ${State}. The area falls under the ${Division} division
          and is part of the ${Circle} postal circle. The designated PIN code for this area is ${Pincode}.
        </p>
        
        <h3 class="text-xl font-semibold mb-3">Postal Information</h3>
        <ul class="list-disc pl-5 mb-4">
          <li>PIN Code: ${Pincode}</li>
          <li>Post Office Name: ${Name}</li>
          <li>District: ${District}</li>
          <li>State: ${State}</li>
          <li>Division: ${Division}</li>
          <li>Region: ${Region}</li>
          ${Block ? `<li>Block: ${Block}</li>` : ''}
        </ul>

        <h3 class="text-xl font-semibold mb-3">Nearby Areas</h3>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
          ${postOffices.slice(1, 7).map(po => `
            <div class="p-3 bg-gray-50 rounded-lg">
              <h4 class="font-medium">${po.Name}</h4>
              <p class="text-sm text-gray-600">PIN: ${po.Pincode}</p>
            </div>
          `).join('')}
        </div>

        <h3 class="text-xl font-semibold mb-3 mt-6">Additional Information</h3>
        <p class="mb-4">
          The post office in ${Name} provides various postal services including:
        </p>
        <ul class="list-disc pl-5">
          <li>Speed Post</li>
          <li>Registered Post</li>
          <li>Money Order</li>
          <li>Postal Savings Schemes</li>
          <li>Parcel Services</li>
        </ul>
      </div>
    `;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>{generateSeoTitle(extractedQuery)}</title>
        <meta
          name="description"
          content={generateSeoDescription(extractedQuery, firstPostOffice?.Pincode)}
        />
        {/* Add structured data for better SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'PostalAddress',
            'addressLocality': firstPostOffice?.Name,
            'addressRegion': firstPostOffice?.State,
            'postalCode': firstPostOffice?.Pincode,
            'addressCountry': 'IN',
            'description': generateSeoDescription(extractedQuery, firstPostOffice?.Pincode)
          })}
        </script>
      </Helmet>

      {isLoading ? (
        <div className="flex justify-center items-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="text-red-600 text-center">
          Error loading data. Please try again.
        </div>
      ) : postOffices.length > 0 ? (
        <div 
          className="prose max-w-none"
          dangerouslySetInnerHTML={{ __html: generateAreaContent() }}
        />
      ) : (
        <div className="text-center text-gray-600">
          No information found for this area.
        </div>
      )}
    </div>
  );
};

export default AreaPincode;
